import { Show, splitProps, type ComponentProps } from "solid-js";
import type { ClassValue } from "~/util/html/style";
import clsx from "~/util/html/style";
import { BaselineAligner } from "~/common/baseline_aligner";
import { destinationAttrs, type LinkDestination } from "~/common/destination";
import { Icon, type IconName } from "~/icon";

export type PillVariant =
  | "plain"
  | "gray"
  | "white"
  | "ai"
  | "success"
  | "successOutline"
  | "danger"
  | "warning"
  | "yellow"
  | "cyan"
  | "rose"
  | "purple"
  | "pink"
  | "primary";

export const PILL_VARIANT_CLASSES: Record<PillVariant, string> = {
  plain: "bg-brand-light",
  // Neutral; plain is faintly blue.
  gray: "bg-brand-disabled text-typography-secondary",
  white: "bg-white border border-brand-subtle text-typography-primary",
  ai: "bg-linear-to-r from-indigo-100 to-purple-200 text-indigo-700",
  success: "bg-brand-success-light text-brand-success-dark",
  successOutline: "border border-brand-success text-brand-success-dark",
  danger: "bg-brand-danger-light text-brand-danger",
  warning: "bg-brand-warning-light text-brand-warning-dark",
  yellow: "bg-yellow-100 text-yellow-800",
  cyan: "bg-brand-cyan text-brand-cyan-dark",
  rose: "bg-rose-100 text-rose-700",
  purple: "bg-purple-100 text-purple-700",
  // Mirrors the Hotwire ui_pill :pink (the verification screen's vendor badge).
  pink: "bg-brand-pink text-typography-primary",
  primary: "bg-brand-primary text-white",
};

// The outlined form of a variant: its text and a faint border in its tone, on no fill.
const PILL_OUTLINE_CLASSES: Record<PillVariant, string> = {
  plain: "border border-brand-subtle text-typography-primary",
  gray: "border border-brand-subtle text-typography-secondary",
  white: "border border-brand-subtle text-typography-primary",
  ai: "border border-indigo-300 text-indigo-700",
  success: "border border-brand-success/30 text-brand-success-dark",
  successOutline: PILL_VARIANT_CLASSES.successOutline,
  danger: "border border-brand-danger/30 text-brand-danger",
  warning: "border border-brand-warning/40 text-brand-warning-dark",
  yellow: "border border-yellow-400/50 text-yellow-800",
  cyan: "border border-brand-cyan-dark/30 text-brand-cyan-dark",
  rose: "border border-rose-300/50 text-rose-700",
  purple: "border border-purple-300/50 text-purple-700",
  pink: "border border-brand-pink text-typography-primary",
  primary: "border border-brand-primary/40 text-brand-primary",
};

// The solid/saturated background for each variant — the counterpart to the pill's soft tint, for
// dots, accents, and other indicators. Lives here so anything using it stays in sync with the pills.
const SOLID_BG_CLASSES: Record<PillVariant, string> = {
  plain: "bg-gray-400",
  gray: "bg-gray-400",
  white: "bg-gray-300",
  ai: "bg-indigo-400",
  success: "bg-brand-success",
  successOutline: "bg-brand-success",
  danger: "bg-brand-danger",
  warning: "bg-brand-warning",
  yellow: "bg-yellow-400",
  cyan: "bg-brand-cyan-dark",
  rose: "bg-rose-400",
  purple: "bg-purple-400",
  pink: "bg-brand-pink",
  primary: "bg-brand-primary",
};

export const bgClassForVariant = (variant: PillVariant): string => SOLID_BG_CLASSES[variant];

// A small solid status dot in a variant's saturated color — the leading accent
// used next to status labels (in pills, confidence indicators, etc).
export function StatusDot(props: { variant: PillVariant; title?: string; class?: ClassValue }) {
  return (
    <span
      class={clsx("size-1.5 shrink-0 rounded-full", bgClassForVariant(props.variant), props.class)}
      title={props.title}
    />
  );
}

type PillProps = ComponentProps<"div"> & {
  // TODO: places use the various pill colors even when things don't mean "success" or "danger", need better names
  variant: PillVariant;
  class?: ClassValue;
  childrenClass?: string;
  size?: "xs" | "sm" | "md";
  icon?: IconName;
  href?: string;
  destination?: LinkDestination;
  clickable?: boolean;
  border?: boolean;
  /** The variant's tone as text and border on no fill, for a status that sits among others. */
  outline?: boolean;
};

export function Pill(props: PillProps) {
  const [local, rest] = splitProps(props, [
    "variant",
    "size",
    "icon",
    "href",
    "destination",
    "clickable",
    "border",
    "outline",
  ]);

  const pill = (
    <div
      {...rest}
      class={clsx(
        // base
        "flex w-fit max-w-full items-center gap-1 truncate rounded-full font-medium whitespace-nowrap",

        // size
        local.size === "xs" && "h-4 px-1.5 text-2xs",
        local.size === "sm" && "h-5 px-2 text-xs",
        (local.size === "md" || !local.size) && "h-7 px-3 text-sm",

        // variant
        local.outline ? PILL_OUTLINE_CLASSES[local.variant] : PILL_VARIANT_CLASSES[local.variant],

        // border
        local.border && "border border-brand-subtle", // TODO: support all variants

        // hover
        (local.href || local.clickable) && "cursor-pointer hover:brightness-95",

        // custom
        rest.class,
      )}
    >
      <Show when={local.icon}>
        {(icon) => (
          <BaselineAligner>
            <Icon name={icon()} size={local.size === "sm" || local.size === "xs" ? "xs" : "sm"} />
          </BaselineAligner>
        )}
      </Show>
      <span class={clsx("truncate", props.childrenClass)}>{rest.children}</span>
    </div>
  );

  return (
    <Show when={local.href} fallback={pill}>
      {(href) => (
        <a
          href={href()}
          data-turbo="false"
          {...destinationAttrs(local.destination)}
          // The anchor needs a bounded box of its own, or the pill's max-w-full measures against an
          // anchor already sized to its overflowing content rather than against the cell.
          class="block w-fit max-w-full min-w-0"
        >
          {pill}
        </a>
      )}
    </Show>
  );
}
